import React, { useState } from 'react';
import { StepProps } from '../../types/onboarding';
import { Vendor, CreateVendorForm, Category } from '../../types';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import { Button } from '../ui/button';
import Modal from '../ui/Modal';
import VendorForm from '../forms/VendorForm';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';

export const VendorStep: React.FC<StepProps> = ({ onNext, onSkip }) => {
  const { token } = useAuth();
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [addedVendors, setAddedVendors] = useState<Vendor[]>([]);
  const [formError, setFormError] = useState<string>('');
  const { refreshStatus } = useOnboarding();

  // Fetch categories for the vendor form
  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(1, 100), // Get all categories
    enabled: !!token && !isSystemAdmin,
  });

  const categories: Category[] = (categoriesResponse as any)?.data?.data || categoriesResponse?.data || [];

  const createVendorMutation = useMutation({
    mutationFn: (data: CreateVendorForm) => {
      return apiService.createVendor(data);
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        toast.success(`Vendor "${response.data.name}" created successfully!`);
        setAddedVendors([...addedVendors, response.data]);
        setShowCreateModal(false);
        setFormError('');
        refreshStatus();
      } else {
        toast.error(response.message || 'Failed to create vendor');
        setFormError(response.message || 'Failed to create vendor');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Error creating vendor';
      toast.error(errorMessage);
      setFormError(errorMessage);
    },
  });

  const handleCreate = (data: CreateVendorForm) => {
    createVendorMutation.mutate(data);
  };

  const handleContinue = () => {
    onNext();
  };

  return (
    <div>
      <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Add Vendors (Optional)
      </h3>
      <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
        Vendors are contractors or service providers you assign to work orders.
        You can add them now or later.
      </p>

      {addedVendors.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Added Vendors:</p>
          <div className="flex flex-wrap gap-2">
            {addedVendors.map((vendor) => (
              <span
                key={vendor.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              >
                {vendor.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <Button
          variant="outline"
          onClick={() => setShowCreateModal(true)}
        >
          Add Vendor
        </Button>
      </div>

      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" onClick={onSkip}>
          Skip for Now
        </Button>
        <Button onClick={handleContinue}>
          {addedVendors.length > 0 ? 'Continue →' : 'Skip & Continue →'}
        </Button>
      </div>

      {/* Vendor Creation Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError('');
        }}
        title="Add New Vendor"
        maxWidth="2xl"
      >
        <VendorForm
          categories={categories}
          onSubmit={handleCreate}
          isLoading={createVendorMutation.isPending}
          error={formError}
        />
      </Modal>
    </div>
  );
};
